// ============================================================
//  MASAMADREAM — Settings Screen
//  Fase 6: nombre del starter, logros, borrar datos
// ============================================================

import { ACHIEVEMENTS } from './achievements.js';
import { toast }         from './toast.js';

/**
 * Wire all settings screen event handlers (call once on init).
 * @param {Store}    store
 * @param {Function} onBack   — called when user taps ← Inicio
 * @param {Function} onReset  — called after clearing all data
 * @param {Function} onNameSaved — called after saving starter name
 */
export function initSettings(store, { onBack, onReset, onNameSaved, onStartDateChanged } = {}) {
  // ── Back button ────────────────────────────────────────────
  document.getElementById('mm-btn-back-from-settings')?.addEventListener('click', () => {
    onBack?.();
  });

  // ── Save name ──────────────────────────────────────────────
  document.getElementById('mm-settings-save-name')?.addEventListener('click', () => {
    const input = document.getElementById('mm-settings-starter-name');
    const name  = (input?.value || '').trim().slice(0, 30);
    store.setStarterName(name);
    toast(`🌱 Nombre guardado: ${name || 'Starter'}`);
    onNameSaved?.(name);
  });

  // ── Save start date ────────────────────────────────────────
  document.getElementById('mm-settings-save-start-date')?.addEventListener('click', () => {
    const input = document.getElementById('mm-settings-start-date');
    if (!input?.value) { toast('⚠️ Selecciona una fecha válida'); return; }

    // datetime-local gives local time — convert to ISO UTC
    const iso = new Date(input.value).toISOString();
    const processId = store.state.viewingId;
    const updated = store.setStartDate(iso, processId);
    if (!updated) { toast('⚠️ No hay proceso activo'); return; }

    _updateStartPreview(updated);
    toast(`📅 Ciclo actualizado — Día ${updated.currentDay}`);
    onStartDateChanged?.(updated);
  });

  // Live preview when user changes the input
  document.getElementById('mm-settings-start-date')?.addEventListener('change', () => {
    const input   = document.getElementById('mm-settings-start-date');
    const preview = document.getElementById('mm-settings-start-preview');
    if (!input?.value || !preview) return;
    const dt = new Date(input.value);
    const process = store.getViewingProcess();
    if (!process) return;
    const maxDays = process.type === 'p1' ? 7 : 5;
    const diffMs  = Date.now() - dt.getTime();
    const day     = Math.min(Math.max(Math.floor(diffMs / 86400000) + 1, 1), maxDays);
    preview.textContent = `→ Día ${day} de ${maxDays}`;
  });

  // ── Clear all data ─────────────────────────────────────────
  document.getElementById('mm-settings-clear-data')?.addEventListener('click', () => {
    if (!confirm('¿Borrar TODOS los datos? Esto eliminará tu proceso activo, historial y logros. Esta acción no se puede deshacer.')) return;
    store.clearAll();
    toast('🗑 Datos eliminados');
    onReset?.();
  });
}

/**
 * Populate / refresh the settings screen content.
 * Call this every time you navigate to the settings screen.
 * @param {Store} store
 */
export function renderSettings(store) {
  const state = store.load();
  const viewingProcess = store.getViewingProcess();

  // Starter name input
  const nameInput = document.getElementById('mm-settings-starter-name');
  if (nameInput) nameInput.value = state.starterName || '';

  // Cycle start date section — only visible when a process is active
  const cycleSection = document.getElementById('mm-settings-cycle-section');
  if (cycleSection) {
    if (viewingProcess) {
      cycleSection.style.display = '';
      const dtInput   = document.getElementById('mm-settings-start-date');
      if (dtInput) dtInput.value = _isoToLocalInput(viewingProcess.startDate);
      _updateStartPreview(viewingProcess);
    } else {
      cycleSection.style.display = 'none';
    }
  }

  // Achievements
  _renderAchievementBadges(store);
}

// ── Private ───────────────────────────────────────────────────

/** Convert ISO string to the value format required by <input type="datetime-local"> (local time, no seconds) */
function _isoToLocalInput(iso) {
  if (!iso) return '';
  const d   = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Update the "→ Día X de Y" preview text below the date input */
function _updateStartPreview(process) {
  const preview = document.getElementById('mm-settings-start-preview');
  if (!preview || !process) return;
  const maxDays = process.type === 'p1' ? 7 : 5;
  preview.textContent = `→ Día ${process.currentDay} de ${maxDays}`;
}

function _renderAchievementBadges(store) {
  const container = document.getElementById('mm-settings-achievements');
  if (!container) return;

  const state    = store.load();
  const unlocked = state.achievements || [];

  container.innerHTML = '';

  if (unlocked.length === 0) {
    container.innerHTML = `
      <div class="mm-empty-state">
        <span class="mm-empty-state-icon">🔒</span>
        <span class="mm-empty-state-text">Todavía no has desbloqueado ningún logro. ¡Completa tu primer proceso!</span>
      </div>`;
    return;
  }

  unlocked.forEach(id => {
    const badge = ACHIEVEMENTS[id];
    if (!badge) return;
    const el = document.createElement('div');
    el.className = 'mm-achievement-badge';
    el.innerHTML = `
      <span class="mm-achievement-emoji" aria-hidden="true">${badge.emoji}</span>
      <div class="mm-achievement-text">
        <div class="mm-achievement-title">${badge.title}</div>
        <div class="mm-achievement-desc">${badge.desc}</div>
      </div>`;
    container.appendChild(el);
  });

  // Show locked badges (greyed out) so users know what to aim for
  const all     = Object.values(ACHIEVEMENTS);
  const locked  = all.filter(b => !unlocked.includes(b.id));
  if (locked.length) {
    const sep = document.createElement('p');
    sep.className = 'mm-settings-locked-label';
    sep.textContent = 'Por desbloquear:';
    container.appendChild(sep);

    locked.forEach(badge => {
      const el = document.createElement('div');
      el.className = 'mm-achievement-badge is-locked';
      el.innerHTML = `
        <span class="mm-achievement-emoji" aria-hidden="true">🔒</span>
        <div class="mm-achievement-text">
          <div class="mm-achievement-title">${badge.title}</div>
          <div class="mm-achievement-desc">${badge.desc}</div>
        </div>`;
      container.appendChild(el);
    });
  }
}
